import { useQuery } from '@apollo/client'
import { useFeatureFlag } from '@faceup/posthog'
import { useNavigate, useSearchParams } from '@faceup/router'
import { Col, Row } from '@faceup/ui-base'
import { useCallback, useContext, useEffect } from 'react'
import { graphql } from '../../__generated__'
import PageTitle from '../../Components/PageTitle'
import { ReportFormContext } from '../../Contexts/ReportFormContext'
import { UserContext } from '../../Contexts/UserContext'
import { useCustomValues } from '../../hooks/useCustomValues'
import { defineMessages, FormattedMessage } from '../../TypedIntl'
import { sizeSettings } from '../../utils/constants'
import { isFromAkutan } from '../../utils/urls'
import { AF_CreateReportForm } from './AF_CreateReportForm'
import { CreateReportForm } from './CreateReportForm'

const messages = defineMessages({
  reportTitle: 'Shared.CreateReport.title',
})

const query = graphql(`
  query CreateReportQuery(
    $reportSourceId: PublicReportSourceGlobalId!
    $institutionId: UUID!
  ) {
    systemInfo {
      id
      publicKey
    }

    publicReportSource(
      reportSourceId: $reportSourceId
      institutionId: $institutionId
    ) {
      id
      formId
      ...CreateReportForm_reportSource
      ...AF_CreateReportForm_categories
      institution {
        id
        type

        ...CreateReportForm_institution
        ...AF_CreateReportForm_institution
      }
    }
  }
`)

const CreateReport = () => {
  const { reportSource: userInstitution } = useContext(UserContext)
  const { setVictimPin, setCreatedAt, setTag } = useContext(ReportFormContext)
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const customValues = useCustomValues()
  const { isFeatureFlagEnabled } = useFeatureFlag()
  const isAbstractFormsEnabled = isFeatureFlagEnabled('abstract-forms')

  const onSent = useCallback(
    (pin: string | null, tag: string) => {
      setVictimPin(pin)
      setCreatedAt(new Date())
      setTag(tag)
      navigate(routes => routes.sentReport())
    },
    [navigate, setCreatedAt, setVictimPin, setTag]
  )

  useEffect(() => {
    if (params.has('testSent') && isFromAkutan(document.referrer)) {
      onSent('1111 2222 3333 4444', 'aaaaa')
    }
  }, [onSent, params])

  const { data, loading } = useQuery(query, {
    variables: {
      reportSourceId: userInstitution?.defaultFormId ?? '',
      institutionId: userInstitution?.institutionId ?? '',
    },
  })

  const reportSource = data?.publicReportSource
  const institution = reportSource?.institution

  if (!reportSource || !institution) {
    return null
  }

  const publicKey = data?.systemInfo?.publicKey ?? ''

  return (
    <>
      <Row justify='center'>
        <Col {...sizeSettings}>
          <PageTitle>
            <FormattedMessage {...messages.reportTitle} />
          </PageTitle>
        </Col>
      </Row>
      <Row justify='center'>
        <Col {...sizeSettings}>
          {isAbstractFormsEnabled ? (
            <AF_CreateReportForm
              formId={reportSource.formId}
              categories={reportSource}
              institution={institution}
              publicKey={publicKey}
              onSent={onSent}
            />
          ) : (
            <CreateReportForm
              loading={loading}
              institution={institution}
              reportSource={reportSource}
              onSent={onSent}
              publicKey={publicKey}
              customValues={customValues}
            />
          )}
        </Col>
      </Row>
    </>
  )
}

export default CreateReport
